import { VehicleModel } from '@/app/models'
import * as React from 'react'
import { createContext, useContext } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import { ViewModeType } from '@/modules/drivers/types'
import {
  useInfiniteQueryVehiclesList,
  useQueryVehicleGroups,
} from '@/lib/api/company/vehicle-list'
import { getVehiclesList } from '@/lib/api/company/vehicle-list/getVehiclesList'
import { useInfiniteQueryVehiclesWithLocksList } from '@/lib/api/company/vehicle-locks-list'
import { getVehiclesWithLocksList } from '@/lib/api/company/vehicle-locks-list/getVehiclesWithLocksList'

interface VehiclesContextProps {
  vehicles: VehicleModel[]
  allVehicles: VehicleModel[]

  selectedIds: string[]
  setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>
  isSelectedAny: boolean
  vehiclesIds: string[]

  actualGroup: VehicleGroupModel | null
  setActualGroup: React.Dispatch<React.SetStateAction<VehicleGroupModel | null>>
  groups: VehicleGroupModel[]
  activeViewMode: ViewModeType
  setActiveViewMode: React.Dispatch<ViewModeType>

  setSearch: any
  search: string
  searchResults: number
  setSelectedVehicleId: React.Dispatch<React.SetStateAction<string>>
  selectedVehicleId: string

  refresh: () => void

 
  fetchAllVehicleIds: () => Promise<string[]>

  totalCount: number

  isLoadingVehicles: boolean

  hasMore: boolean
  loadMore: () => void
  isLoadingMore: boolean
}

const VehiclesContext = createContext<VehiclesContextProps | null>(null)

export const VehiclesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  // Router hooks — no prop drilling. Effects below depend on `pathname`
  // only so they don't re-run on every router state change.
  const { pathname } = useLocation()
  const { companyId = '' } = useParams<{ companyId: string }>()

  const [actualGroup, setActualGroup] = React.useState<VehicleGroupModel | null>(
    { id: -1, name: 'Aktywni' },
  )
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])

  const [search, setSearch] = React.useState('')

  // --- Route-derived flags ---
  const isVehicleRoute = React.useMemo(
    () => pathname.includes('/vehicleModule'),
    [pathname],
  )
  const isBlocksRoute = React.useMemo(
    () => pathname.includes('/vehicleModule/blocks'),
    [pathname],
  )
  const activeViewMode = React.useMemo<ViewModeType>(
    () => (/vehicle\/[^/]+/.test(pathname) ? 'single' : 'group'),
    [pathname],
  )
  const setActiveViewMode = React.useCallback<
    React.Dispatch<ViewModeType>
  >(() => {}, [])

  const isActiveFilter = React.useMemo(() => {
    if (!actualGroup) return undefined
    if (actualGroup.id === -1) return true
    if (actualGroup.id === -2) return false
    return undefined
  }, [actualGroup])

  const sharedParams = {
    companyId,
    pageSize: 50,
    groupId: actualGroup?.id && actualGroup.id > 0 ? actualGroup.id : undefined,
    isActive: isActiveFilter,
    search: search && search.trim() !== '' ? search.trim() : undefined,
  }

  // Two parallel infinite queries — only the one matching the current route
  // is `enabled`; we pick its result below.
  const vehiclesQuery = useInfiniteQueryVehiclesList({
    ...sharedParams,
    enabled: isVehicleRoute && !isBlocksRoute && !!companyId,
  })
  const locksQuery = useInfiniteQueryVehiclesWithLocksList({
    ...sharedParams,
    enabled: isVehicleRoute && isBlocksRoute && !!companyId,
  })
  const active = isBlocksRoute ? locksQuery : vehiclesQuery
  const {
    vehicles: rawVehicles,
    totalCount,
    isLoading: isLoadingVehicles,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = active

  const vehicles = React.useMemo(
    () =>
      (rawVehicles as any[]).map((v) => ({
        ...v,
        id: typeof v.id === 'number' ? String(v.id) : v.id,
      })) as VehicleModel[],
    [rawVehicles],
  )
  const allVehicles = vehicles

  const vehiclesIds = React.useMemo(
    () => Array.from(new Set(vehicles.map((v) => v.id))),
    [vehicles],
  )

  const isSelectedAny = selectedIds.length > 0
  const hasMore = !!hasNextPage
  const isLoadingMore = isFetchingNextPage
  const loadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const searchResults = totalCount

  // --- Selected vehicle — URL is source of truth ---
  const { vehicleId: vehicleIdParam } = useParams<{ vehicleId?: string }>()
  const [manuallySelected, setManuallySelected] = React.useState('')
  const selectedVehicleId = React.useMemo(() => {
    if (vehicleIdParam) return vehicleIdParam
    if (manuallySelected && vehiclesIds.includes(manuallySelected)) {
      return manuallySelected
    }
    const sorted = [...vehiclesIds].sort()
    return sorted[0] || ''
  }, [vehicleIdParam, manuallySelected, vehiclesIds])
  const setSelectedVehicleId = setManuallySelected

  // --- Groups via react-query ---
  // `groups` is derived directly from the query result; passing it to a
  // `useEffect` dep triggered an infinite loop because the `|| []` default
  // produced a fresh array reference on every render while `data` was
  // undefined. The original intent was "clear per-company selection on
  // company change" — keyed on `companyId` directly.
  const { data: groupsData, refetch: refetchGroups } = useQueryVehicleGroups({
    companyId,
  })
  const groups = groupsData ?? []
  React.useEffect(() => {
    setSelectedIds([])
  }, [companyId])

  const refresh = React.useCallback(() => {
    void refetchGroups()
    void refetch()
  }, [refetchGroups, refetch])

  const fetchAllVehicleIds = React.useCallback(async (): Promise<string[]> => {
    if (!companyId) return []
    const params = {
      companyId,
      groupId:
        actualGroup?.id && actualGroup.id > 0 ? actualGroup.id : undefined,
      isActive: isActiveFilter,
      search: search && search.trim() !== '' ? search.trim() : undefined,
    }
    const res: any = isBlocksRoute
      ? await getVehiclesWithLocksList(params)
      : await getVehiclesList(params)
    const data = res?.data
    const list: any[] = Array.isArray(data) ? data : (data?.vehicles ?? [])
    return Array.from(new Set(list.map((v) => String(v.id))))
  }, [companyId, actualGroup, isActiveFilter, search, isBlocksRoute])

  return (
    <VehiclesContext.Provider
      value={{
        vehicles,
        allVehicles,
        selectedIds,
        setSelectedIds,
        vehiclesIds,
        isSelectedAny,
        groups,
        actualGroup,
        setActualGroup,
        activeViewMode,
        setActiveViewMode,
        search,
        setSearch,
        searchResults,
        setSelectedVehicleId,
        selectedVehicleId,
        refresh,
        fetchAllVehicleIds,
        totalCount,
        isLoadingVehicles,
        hasMore,
        loadMore,
        isLoadingMore,
      }}
    >
      {children}
    </VehiclesContext.Provider>
  )
}

export const useVehicles = () => {
  const ctx = useContext(VehiclesContext)
  if (!ctx) throw new Error('useVehicles must be used inside <VehiclesProvider>')
  return ctx
}
